import { useMemo, useState } from "react";
import {
  Gift,
  Crown,
  Users,
  Smartphone,
  MessageCircle,
  Trophy,
  Zap,
  Star,
  ArrowRight,
  Mail,
  Phone,
  Send,
  Clock,
  ShieldCheck,
  CheckCircle2,
  Download,
  QrCode,
  HeadphonesIcon,
  Facebook,
  Instagram,
  ChevronRight,
  Sparkles,
  Flame,
  Wallet,
  CircleDollarSign,
  Gem,
  Timer,
  BadgeCheck,
  Layers,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SiteLayout } from "../components/site/SiteLayout";
import { PROMOTIONS, AMBASSADORS } from "../data/site";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { resolveIcon } from "../lib/iconMap";

/* =============================================================
   Shared Hero header — reused across pages for consistency
   ============================================================= */
const PageHero = ({ icon: Icon, title, subtitle, accent = "#19a55c", kicker }) => (
  <section
    className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/5 p-5 sm:p-10 mb-5 sm:mb-8"
    style={{
      background: `radial-gradient(120% 100% at 0% 0%, ${accent}22, transparent 55%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)`,
    }}
  >
    <div
      aria-hidden
      className="absolute -top-20 -right-10 h-56 w-56 sm:h-80 sm:w-80 rounded-full opacity-20 blur-3xl"
      style={{ background: accent }}
    />
    <div className="relative flex items-center gap-3 sm:gap-4">
      <div
        className="shrink-0 h-12 w-12 sm:h-16 sm:w-16 rounded-xl sm:rounded-2xl flex items-center justify-center"
        style={{ background: `${accent}1f`, boxShadow: `inset 0 0 0 1px ${accent}55` }}
      >
        <Icon size={26} style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        {kicker && (
          <div className="text-[11px] sm:text-xs uppercase tracking-widest font-bold mb-1" style={{ color: accent }}>
            {kicker}
          </div>
        )}
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-white/60 mt-1.5 sm:mt-2 text-sm sm:text-base max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </section>
);

/* =============================================================
   PROMOTIONS PAGE — Premium redesign
   ============================================================= */

// Map a badge label to a category + icon for filtering
const BADGE_META = {
  NEW: { cat: "new", icon: Sparkles, glow: "#19a55c" },
  HOT: { cat: "hot", icon: Flame, glow: "#ff6b35" },
  VIP: { cat: "vip", icon: Crown, glow: "#a855f7" },
  EVENT: { cat: "event", icon: Trophy, glow: "#ef4444" },
  EXCLUSIVE: { cat: "exclusive", icon: Gem, glow: "#06b6d4" },
};

const FILTER_TABS = [
  { id: "all", label: "All Promotions", icon: Layers },
  { id: "new", label: "New", icon: Sparkles },
  { id: "hot", label: "Hot", icon: Flame },
  { id: "vip", label: "VIP", icon: Crown },
  { id: "event", label: "Tournaments", icon: Trophy },
];

const PromoCard = ({ p, onClaim, featured = false }) => {
  const meta = BADGE_META[(p.badge || "").toUpperCase()] || { icon: Sparkles, glow: p.color1 };
  const BadgeIcon = meta.icon;
  return (
    <div
      className={`group relative rounded-3xl overflow-hidden border border-white/[0.06] hover:border-white/20 transition-all duration-500 hover:-translate-y-1.5 ${
        featured ? "min-h-[300px] sm:min-h-[380px]" : "min-h-[240px] sm:min-h-[280px]"
      }`}
      style={{
        background: `linear-gradient(135deg, ${p.color1} 0%, ${p.color2} 100%)`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 48px -24px ${p.color1}55`,
      }}
    >
      {/* mesh light layer */}
      <div
        aria-hidden
        className="absolute -right-20 -top-20 h-72 w-72 sm:h-96 sm:w-96 rounded-full opacity-30 blur-2xl"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.55), transparent 70%)" }}
      />
      {/* dotted noise texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
          backgroundSize: "10px 10px",
        }}
      />
      {/* hover shine sweep */}
      <div
        aria-hidden
        className="absolute -inset-x-12 top-0 h-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.18) 45%, rgba(255,255,255,0.32) 50%, rgba(255,255,255,0.18) 55%, transparent 70%)",
          transform: "translateX(-30%)",
          animation: "promoShine 2s ease-out infinite",
        }}
      />

      {/* corner ribbon (limited/event style) for featured */}
      {featured && (
        <div className="absolute top-5 right-5 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/15">
          <Timer size={12} className="text-white" />
          <span className="text-[10px] font-extrabold tracking-widest text-white uppercase">
            Limited Time
          </span>
        </div>
      )}

      <div className={`relative z-10 ${featured ? "p-7 sm:p-10" : "p-5 sm:p-7"} flex flex-col h-full`}>
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold tracking-[0.18em] bg-black/45 text-white backdrop-blur-md border border-white/10"
          >
            <BadgeIcon size={11} className="text-white" />
            {p.badge}
          </span>
        </div>

        <h3
          className={`font-extrabold tracking-tight text-white leading-[1.05] ${
            featured ? "text-3xl sm:text-5xl lg:text-6xl" : "text-xl sm:text-2xl lg:text-3xl"
          }`}
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.25)" }}
        >
          {p.title}
        </h3>

        <p className={`text-white/90 mt-3 max-w-md ${featured ? "text-base sm:text-lg" : "text-sm sm:text-base"}`}>
          {p.subtitle}
        </p>

        {/* feature chips (only featured) */}
        {featured && (
          <div className="mt-5 flex flex-wrap gap-2">
            {[
              { icon: BadgeCheck, label: "No-deposit start" },
              { icon: Wallet, label: "Instant credit" },
              { icon: ShieldCheck, label: "Fair wagering" },
            ].map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/12 text-white text-[11px] sm:text-xs font-semibold backdrop-blur-md border border-white/15"
              >
                <c.icon size={12} /> {c.label}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => onClaim(p)}
            data-testid={`promo-claim-${p.id}`}
            className={`group/btn inline-flex items-center gap-2 rounded-xl bg-white text-[#0a1119] font-bold transition-all duration-200 hover:gap-3 hover:bg-white/95 hover:shadow-[0_8px_28px_-6px_rgba(255,255,255,0.5)] active:translate-y-px ${
              featured ? "h-12 px-6 text-sm sm:text-base" : "h-10 sm:h-11 px-4 sm:px-5 text-sm"
            }`}
          >
            {p.cta}
            <ArrowRight size={featured ? 17 : 15} className="transition-transform group-hover/btn:translate-x-0.5" />
          </button>
          <span className="text-[11px] sm:text-xs text-white/75 font-semibold inline-flex items-center gap-1">
            <ShieldCheck size={11} /> T&amp;C apply
          </span>
        </div>
      </div>

      {/* keyframes */}
      <style>{`@keyframes promoShine{0%{transform:translateX(-30%)}60%,100%{transform:translateX(130%)}}`}</style>
    </div>
  );
};

// Stat tile with icon glow
const StatTile = ({ value, label, color, Icon }) => (
  <div
    className="group relative rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0f1722] p-4 sm:p-5 transition-all hover:-translate-y-0.5 hover:border-white/15"
    style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)" }}
  >
    <div
      aria-hidden
      className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-25 blur-2xl transition-opacity group-hover:opacity-40"
      style={{ background: color }}
    />
    <div className="relative flex items-center gap-3">
      <div
        className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}1f`, boxShadow: `inset 0 0 0 1px ${color}40` }}
      >
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl sm:text-3xl font-black leading-none" style={{ color }}>
          {value}
        </div>
        <div className="text-[10px] sm:text-[11px] text-white/55 uppercase tracking-[0.14em] font-bold mt-1.5">
          {label}
        </div>
      </div>
    </div>
  </div>
);

const HOW_STEPS = [
  { n: "01", title: "Sign up & Deposit", desc: "Create your free account in 30 seconds and fund your wallet via any local gateway.", icon: Wallet, color: "#19a55c" },
  { n: "02", title: "Claim your bonus", desc: "Pick the offer that fits your style. Hit the Claim button — credit is instant.", icon: Gift, color: "#ffc933" },
  { n: "03", title: "Play & Withdraw", desc: "Meet the simple wagering and cash out winnings within minutes — guaranteed.", icon: CircleDollarSign, color: "#3b82f6" },
];

export const PromotionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { settings } = useSiteSettings() || {};
  const s = settings || {};
  const items = (s.promotions_items?.length ? s.promotions_items : PROMOTIONS).filter((p) => p.enabled !== false);
  const stats = s.promotions_stats?.length ? s.promotions_stats : [
    { value: "100%", label: "Welcome Bonus",       color: "#19a55c" },
    { value: "5%",   label: "Daily Cashback",      color: "#ffc933" },
    { value: "10%",  label: "Referral Commission", color: "#3b82f6" },
    { value: "24/7", label: "Tournaments",         color: "#ec4899" },
  ];
  const STAT_ICONS = [Gift, Wallet, Users, Trophy];

  // Admin-editable hero & extras (with safe fallbacks)
  const heroKicker      = s.promotions_hero_kicker      || "Live Bonuses & Rewards";
  const heroTitle1      = s.promotions_hero_title_1     || "Bigger wins,";
  const heroTitle2      = s.promotions_hero_title_2     || "bolder bonuses.";
  const heroSubtitle    = s.promotions_hero_subtitle    || "Hand-picked promotions for sportsbook, casino, slots and tournaments — credited instantly with simple wagering and same-day cashouts.";
  const heroCtaPrimary  = s.promotions_hero_cta_primary_text   || "Claim Top Bonus";
  const heroCtaSecText  = s.promotions_hero_cta_secondary_text || "Explore VIP Club";
  const heroCtaSecUrl   = s.promotions_hero_cta_secondary_url  || "/vip-club";
  const prizeLabel      = s.promotions_prize_pool_label    || "Total Prize Pool · This Week";
  const prizeAmount     = s.promotions_prize_pool_amount   || "50,00,000";
  const prizeCurrency   = s.promotions_prize_pool_currency || "৳";
  const prizeBreakdown  = (s.promotions_prize_pool_breakdown && s.promotions_prize_pool_breakdown.length)
    ? s.promotions_prize_pool_breakdown
    : [{ label: "Sportsbook", value: "20L" }, { label: "Casino", value: "20L" }, { label: "Slots", value: "10L" }];
  const prizeNote       = s.promotions_prize_pool_note  || "Refreshes every Monday · 00:00 Asia/Dhaka";

  // How-to-claim steps with icon-name → component map
  const STEP_ICON_MAP = { Wallet, Gift, CircleDollarSign, BadgeCheck, ShieldCheck, Sparkles, Trophy, Users };
  const howStepsRaw = (s.promotions_how_steps && s.promotions_how_steps.length) ? s.promotions_how_steps : [
    { n: "01", title: "Sign up & Deposit", desc: "Create your free account in 30 seconds and fund your wallet via any local gateway.", icon: "Wallet",            color: "#19a55c" },
    { n: "02", title: "Claim your bonus",  desc: "Pick the offer that fits your style. Hit the Claim button — credit is instant.",       icon: "Gift",              color: "#ffc933" },
    { n: "03", title: "Play & Withdraw",   desc: "Meet the simple wagering and cash out winnings within minutes — guaranteed.",          icon: "CircleDollarSign",  color: "#3b82f6" },
  ];
  const howSteps = howStepsRaw.map((st) => ({ ...st, IconComp: STEP_ICON_MAP[st.icon] || Wallet }));

  const termsLines = (s.promotions_terms_lines && s.promotions_terms_lines.length) ? s.promotions_terms_lines : [
    "All bonuses require a minimum qualifying deposit as specified in each promotion.",
    "Wagering / turnover multiplier applies before any bonus funds can be withdrawn.",
    "Maxlive88 reserves the right to modify or cancel promotions at any time.",
    "Only open to players aged 18 and above. Play responsibly.",
  ];

  const [filter, setFilter] = useState("all");
  const visibleItems = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((p) => {
      const cat = (BADGE_META[(p.badge || "").toUpperCase()] || {}).cat;
      return cat === filter;
    });
  }, [items, filter]);

  const featured = visibleItems[0];
  const rest = visibleItems.slice(1);

  if (s.promotions_enabled === false) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-white/70">
          Promotions are currently unavailable. Please check back soon.
        </div>
      </SiteLayout>
    );
  }

  const onClaim = (p) => {
    if (!user) {
      toast.info("Please log in to claim your bonus");
      navigate("/login");
      return;
    }
    toast.success(`${p.title} — claim request submitted`);
    navigate("/profile/deposit");
  };

  return (
    <SiteLayout>
      <div data-testid="promotions-page" className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {/* ===================== Premium HERO ===================== */}
        <section
          className="relative overflow-hidden rounded-3xl border border-white/[0.06] mb-6 sm:mb-9"
          style={{
            background:
              "radial-gradient(120% 100% at 0% 0%, rgba(25,165,92,0.18), transparent 55%), radial-gradient(120% 100% at 100% 100%, rgba(255,201,51,0.10), transparent 60%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)",
          }}
        >
          {/* grid lines */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            }}
          />
          {/* glow blobs */}
          <div aria-hidden className="absolute -top-24 -right-16 h-72 w-72 sm:h-[420px] sm:w-[420px] rounded-full opacity-20 blur-3xl" style={{ background: "#19a55c" }} />
          <div aria-hidden className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full opacity-15 blur-3xl" style={{ background: "#ffc933" }} />

          <div className="relative px-5 sm:px-10 py-8 sm:py-14 flex flex-col lg:flex-row lg:items-center gap-7 lg:gap-12">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-4 sm:mb-5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#19a55c] animate-ping opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#19a55c]" />
                </span>
                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-white/80">
                  {heroKicker}
                </span>
              </div>

              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[0.95]">
                {heroTitle1}{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(135deg, #19a55c 0%, #ffc933 100%)" }}
                >
                  {heroTitle2}
                </span>
              </h1>

              <p className="text-white/70 mt-4 sm:mt-5 text-sm sm:text-lg leading-relaxed max-w-2xl">
                {heroSubtitle}
              </p>

              <div className="mt-6 sm:mt-7 flex flex-wrap gap-2.5">
                <button
                  onClick={() => onClaim(featured || items[0])}
                  data-testid="promo-hero-cta"
                  className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-xl bg-[#19a55c] text-white font-bold text-sm sm:text-base hover:bg-[#15924f] transition-all hover:gap-3 hover:shadow-[0_10px_32px_-8px_rgba(25,165,92,0.6)] active:translate-y-px"
                >
                  {heroCtaPrimary} <ArrowRight size={17} />
                </button>
                <Link
                  to={heroCtaSecUrl}
                  className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm sm:text-base transition-colors"
                >
                  <Crown size={16} className="text-[#ffc933]" /> {heroCtaSecText}
                </Link>
              </div>
            </div>

            {/* Highlight prize tile */}
            <div className="lg:w-[360px] shrink-0">
              <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#101a26] to-[#0a1119] p-5 sm:p-6 overflow-hidden">
                <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-[#ffc933]/20 blur-2xl" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">
                  {prizeLabel}
                </div>
                <div className="mt-2 text-3xl sm:text-4xl font-black text-white">
                  {prizeCurrency} <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #ffc933, #ff8a00)" }}>
                    {prizeAmount}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {prizeBreakdown.slice(0, 3).map((b, i) => (
                    <div key={i} className="rounded-lg bg-white/[0.04] border border-white/[0.06] py-2.5">
                      <div className="text-sm font-extrabold text-white">{prizeCurrency}{b.value}</div>
                      <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">{b.label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2 text-[11px] text-white/55">
                  <Timer size={12} className="text-[#19a55c]" />
                  {prizeNote}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== Stat tiles ===================== */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-6 sm:mb-8">
          {stats.map((s, i) => (
            <StatTile
              key={i}
              value={s.value}
              label={s.label}
              color={s.color}
              Icon={STAT_ICONS[i % STAT_ICONS.length]}
            />
          ))}
        </div>

        {/* ===================== Filter chips ===================== */}
        <div className="flex items-center gap-2 mb-5 sm:mb-6 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {FILTER_TABS.map((t) => {
            const active = filter === t.id;
            const TIcon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                data-testid={`promo-filter-${t.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 h-9 sm:h-10 px-3.5 sm:px-4 rounded-full text-xs sm:text-[13px] font-bold transition-all border ${
                  active
                    ? "bg-white text-[#0a1119] border-white shadow-[0_4px_18px_-4px_rgba(255,255,255,0.4)]"
                    : "bg-white/[0.04] text-white/75 border-white/10 hover:bg-white/[0.08] hover:text-white"
                }`}
              >
                <TIcon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* ===================== Featured + grid ===================== */}
        {visibleItems.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-[#0f1722] p-10 text-center text-white/50">
            No promotions match this filter right now. Try another category.
          </div>
        ) : (
          <>
            {featured && (
              <div className="mb-3 sm:mb-4">
                <PromoCard p={featured} onClaim={onClaim} featured />
              </div>
            )}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {rest.map((p) => (
                  <PromoCard key={p.id} p={p} onClaim={onClaim} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ===================== How to claim ===================== */}
        <section className="mt-9 sm:mt-12">
          <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3">
            <div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#19a55c] mb-1.5">
                Three Easy Steps
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                How to claim your bonus
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
            {howSteps.map((st) => (
              <div
                key={st.n}
                className="relative rounded-2xl border border-white/[0.06] bg-[#0f1722] p-5 sm:p-6 overflow-hidden hover:border-white/15 transition-colors"
              >
                <div
                  aria-hidden
                  className="absolute -top-8 -right-8 h-28 w-28 rounded-full opacity-15 blur-2xl"
                  style={{ background: st.color }}
                />
                <div className="relative flex items-start gap-4">
                  <div
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${st.color}1c`, boxShadow: `inset 0 0 0 1px ${st.color}50` }}
                  >
                    <st.IconComp size={20} style={{ color: st.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: st.color }}>
                      Step {st.n}
                    </div>
                    <h3 className="text-white font-bold text-base sm:text-lg mt-1">{st.title}</h3>
                    <p className="text-white/60 text-[13px] sm:text-sm mt-1.5 leading-relaxed">{st.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===================== Premium T&C ===================== */}
        <section className="mt-8 sm:mt-10">
          <div
            className="rounded-2xl border border-white/[0.06] overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(25,165,92,0.07), transparent 40%), linear-gradient(135deg, #0f1722, #0a1119)",
            }}
          >
            <div className="px-5 sm:px-7 py-5 sm:py-6 border-b border-white/[0.06] flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#19a55c]/15 flex items-center justify-center" style={{ boxShadow: "inset 0 0 0 1px rgba(25,165,92,0.45)" }}>
                <ShieldCheck size={18} className="text-[#19a55c]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm sm:text-base">General Terms & Conditions</h3>
                <p className="text-white/50 text-[11px] sm:text-xs">Applies to all promotions listed on this page.</p>
              </div>
            </div>
            <ul className="px-5 sm:px-7 py-4 sm:py-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {termsLines.map((line, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[12px] sm:text-[13px] text-white/65 leading-relaxed">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#19a55c]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
};

/* =============================================================
   VIP CLUB PAGE
   ============================================================= */
/* =============================================================
   VIP TIER — Premium luxury card
   ============================================================= */
const VipTier = ({ name, range, perks, color, isCurrent, isFlagship = false, index = 0 }) => (
  <div
    data-testid={`vip-tier-${name.toLowerCase()}`}
    className={`group relative rounded-2xl border overflow-hidden transition-all hover:-translate-y-1.5 hover:scale-[1.02] duration-500 ${
      isFlagship ? "lg:scale-[1.03]" : ""
    }`}
    style={{
      background: `linear-gradient(180deg, ${color}11 0%, #0a0f1a 60%, #07101c 100%)`,
      borderColor: isCurrent ? color : `${color}30`,
      boxShadow: isCurrent
        ? `0 14px 40px ${color}45, inset 0 0 0 1px ${color}`
        : `0 6px 22px rgba(0,0,0,0.45)`,
      animation: `ambassadorRise 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 90}ms backwards`,
    }}
  >
    {/* Decorative top-gradient strip */}
    <div
      aria-hidden
      className="absolute inset-x-0 top-0 h-1"
      style={{
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
      }}
    />
    {/* Diagonal pattern */}
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 1px, transparent 1px 14px)`,
      }}
    />
    {/* Radial glow on hover */}
    <div
      aria-hidden
      className="absolute -top-16 -right-16 h-44 w-44 rounded-full blur-3xl opacity-20 group-hover:opacity-50 transition-opacity duration-700"
      style={{ background: color }}
    />

    {/* Top badges */}
    {isCurrent && (
      <span
        className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider uppercase"
        style={{ background: color, color: "#0a1119" }}
      >
        <BadgeCheck size={10} /> Current
      </span>
    )}
    {isFlagship && !isCurrent && (
      <span
        className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider uppercase"
        style={{
          background: `${color}1f`,
          color: color,
          border: `1px solid ${color}55`,
        }}
      >
        <Sparkles size={10} /> Flagship
      </span>
    )}

    <div className="relative p-5 sm:p-6">
      {/* Icon ring */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center"
          style={{
            background: `${color}1f`,
            boxShadow: `inset 0 0 0 1px ${color}66, 0 6px 18px ${color}28`,
          }}
        >
          <Crown size={22} style={{ color }} className="sm:hidden" />
          <Crown size={26} style={{ color }} className="hidden sm:block" />
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.22em] font-black"
          style={{ color: `${color}aa` }}
        >
          Tier {index + 1}
        </span>
      </div>

      {/* Tier name */}
      <h3
        className="text-2xl sm:text-3xl font-black tracking-tight leading-none"
        style={{
          background: `linear-gradient(135deg, ${color} 0%, #ffffff 130%)`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontFamily: "'Playfair Display', Georgia, serif",
        }}
      >
        {name}
      </h3>
      <p className="text-white/45 text-[11px] sm:text-xs mt-1.5 font-mono tracking-wider">
        {range}
      </p>

      {/* Divider */}
      <div
        aria-hidden
        className="my-4 sm:my-5 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${color}55, transparent)`,
        }}
      />

      {/* Perks */}
      <ul className="space-y-2.5 sm:space-y-3">
        {perks.map((p, i) => (
          <li
            key={p}
            className="text-[12.5px] sm:text-sm text-white/80 flex items-start gap-2.5 leading-snug"
          >
            <span
              className="shrink-0 mt-0.5 h-4 w-4 rounded-full flex items-center justify-center"
              style={{
                background: `${color}22`,
                boxShadow: `inset 0 0 0 1px ${color}66`,
              }}
            >
              <CheckCircle2 size={10} style={{ color }} />
            </span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* =============================================================
   VIP PAGE — Premium luxury redesign
   ============================================================= */
const VIP_BENEFITS_OVERVIEW = [
  { icon: Trophy, label: "4 Elite Tiers", color: "#ffc933" },
  { icon: Gem, label: "1.0% Max Cashback", color: "#22d3ee" },
  { icon: Crown, label: "Dedicated Host", color: "#a855f7" },
  { icon: Sparkles, label: "Luxury Travel", color: "#ec4899" },
];

const VIP_LADDER_STEPS = [
  {
    n: "01",
    t: "Deposit & Play",
    d: "Every deposit and bet contributes to your VIP points balance.",
    icon: Wallet,
    color: "#22c55e",
  },
  {
    n: "02",
    t: "Earn VIP Points",
    d: "1 point per ৳100 wagered. Points stack across all games.",
    icon: Zap,
    color: "#ffc933",
  },
  {
    n: "03",
    t: "Unlock Tier",
    d: "Reach the next tier threshold and instantly get all perks.",
    icon: Crown,
    color: "#a855f7",
  },
];

export const VipPage = () => {
  const { user } = useAuth() || {};
  const { settings } = useSiteSettings() || {};
  const navigate = useNavigate();
  const balance = user?.balance || 0;

  const tiersRaw = settings?.vip_tiers?.length ? settings.vip_tiers : [
    { name: "Silver",   range: "৳0 – ৳50,000",           min_balance: 0,       color: "#a8b3bd", perks: ["Daily cashback 0.3%", "Weekly reload bonus", "Birthday gift voucher", "Standard support"], enabled: true },
    { name: "Gold",     range: "৳50,000 – ৳2,00,000",    min_balance: 50000,   color: "#ffc933", perks: ["Daily cashback 0.5%", "Personal VIP manager", "Faster withdrawal (2h)", "Monthly reload bonus"], enabled: true },
    { name: "Platinum", range: "৳2,00,000 – ৳10,00,000", min_balance: 200000,  color: "#a855f7", perks: ["Daily cashback 0.8%", "VIP-only tournaments", "Custom bespoke bonuses", "24/7 priority support"], enabled: true },
    { name: "Diamond",  range: "৳10,00,000+",            min_balance: 1000000, color: "#22d3ee", perks: ["Daily cashback 1.0%", "Luxury gifts & travel", "Private high-roller events", "Dedicated VIP host"], enabled: true },
  ];
  const tiers = tiersRaw.filter((t) => t.enabled !== false);

  if (settings?.vip_enabled === false || !tiers.length) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-white/70">
          VIP Club information is currently unavailable. Please check back soon.
        </div>
      </SiteLayout>
    );
  }

  // Determine current tier from balance (highest tier the user has qualified for)
  let currentTier = tiers[0]?.name || "Silver";
  let currentTierObj = tiers[0];
  let nextTierObj = null;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (balance >= (Number(t.min_balance) || 0)) {
      currentTier = t.name;
      currentTierObj = t;
      nextTierObj = tiers[i + 1] || null;
    }
  }
  const progressPct = nextTierObj
    ? Math.min(
        100,
        Math.round(
          ((balance - (Number(currentTierObj?.min_balance) || 0)) /
            ((Number(nextTierObj?.min_balance) || 0) -
              (Number(currentTierObj?.min_balance) || 0))) *
            100
        )
      )
    : 100;

  return (
    <SiteLayout>
      <div data-testid="vip-page" className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {/* HERO — luxury editorial */}
        <section
          className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
          style={{
            background:
              "radial-gradient(120% 100% at 0% 0%, rgba(255,201,51,0.18), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle, #ffc933 1px, transparent 1.2px)",
              backgroundSize: "26px 26px",
            }}
          />
          <div
            aria-hidden
            className="absolute -top-20 -right-12 h-80 w-80 rounded-full opacity-25 blur-3xl"
            style={{ background: "#ffc933" }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-12 h-72 w-72 rounded-full opacity-15 blur-3xl"
            style={{ background: "#a855f7" }}
          />

          <div className="relative p-6 sm:p-10 lg:p-14">
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <Crown size={14} className="text-[#ffc933]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black text-[#ffc933]">
                Maxlive88 VIP · Invite-only Elite
              </span>
              <span
                className="h-px w-10"
                style={{
                  background:
                    "linear-gradient(90deg, #ffc933, transparent)",
                }}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
              <div className="lg:col-span-7">
                <h1
                  className="text-white font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Play like{" "}
                  <span
                    className="italic relative inline-block"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffc933 0%, #f59e0b 40%, #a855f7 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    royalty
                    <span
                      aria-hidden
                      className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, #ffc933 30%, #a855f7 70%, transparent)",
                      }}
                    />
                  </span>
                  .
                </h1>
                <p className="text-white/65 mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed">
                  The more you play, the more you earn. Climb our{" "}
                  <span className="text-white font-semibold">4 elite tiers</span>{" "}
                  and unlock cashback, dedicated VIP hosts, custom bonuses and
                  legendary high-roller experiences.
                </p>
              </div>
              <div className="lg:col-span-5 grid grid-cols-2 gap-2 sm:gap-3">
                {VIP_BENEFITS_OVERVIEW.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.label}
                      className="relative rounded-xl border border-white/[0.07] px-3 py-3.5 sm:px-4 overflow-hidden"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 60%), #0e1522",
                      }}
                    >
                      <div
                        aria-hidden
                        className="absolute -top-6 -right-6 h-20 w-20 rounded-full blur-2xl opacity-25"
                        style={{ background: b.color }}
                      />
                      <div className="relative flex items-center gap-2.5">
                        <div
                          className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
                          style={{
                            background: `${b.color}1f`,
                            boxShadow: `inset 0 0 0 1px ${b.color}55`,
                          }}
                        >
                          <Icon size={14} style={{ color: b.color }} />
                        </div>
                        <div className="text-white text-xs sm:text-sm font-bold leading-tight">
                          {b.label}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CURRENT TIER + PROGRESS */}
        {user && (
          <section
            data-testid="vip-current-tier"
            className="relative mb-6 sm:mb-8 rounded-2xl border overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,201,51,0.08) 0%, rgba(168,85,247,0.04) 100%), #0d1421",
              borderColor: "rgba(255,201,51,0.30)",
            }}
          >
            <div
              aria-hidden
              className="absolute -top-24 right-0 h-60 w-60 rounded-full opacity-20 blur-3xl"
              style={{ background: currentTierObj?.color || "#ffc933" }}
            />
            <div className="relative p-5 sm:p-7 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
              <div
                className="shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: `${currentTierObj?.color || "#ffc933"}22`,
                  boxShadow: `inset 0 0 0 1px ${currentTierObj?.color || "#ffc933"}66, 0 8px 24px ${currentTierObj?.color || "#ffc933"}33`,
                }}
              >
                <Crown
                  size={28}
                  style={{ color: currentTierObj?.color || "#ffc933" }}
                />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold mb-1">
                  Your current tier
                </div>
                <div className="flex items-baseline gap-3">
                  <span
                    className="text-2xl sm:text-3xl font-black"
                    style={{
                      color: currentTierObj?.color || "#ffc933",
                      fontFamily: "'Playfair Display', Georgia, serif",
                    }}
                  >
                    {currentTier}
                  </span>
                  {nextTierObj && (
                    <span className="text-white/45 text-xs sm:text-sm">
                      → next: <span className="text-white/85 font-semibold">{nextTierObj.name}</span>
                    </span>
                  )}
                </div>
                {nextTierObj && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[11px] sm:text-xs text-white/55 mb-1.5 font-semibold">
                      <span>৳{Number(currentTierObj?.min_balance || 0).toLocaleString("en-IN")}</span>
                      <span className="text-white/85">{progressPct}%</span>
                      <span>৳{Number(nextTierObj?.min_balance || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="relative h-2 rounded-full overflow-hidden bg-white/[0.06]">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all"
                        style={{
                          width: `${progressPct}%`,
                          background: `linear-gradient(90deg, ${currentTierObj?.color || "#ffc933"}, ${nextTierObj?.color || "#a855f7"})`,
                          boxShadow: `0 0 12px ${currentTierObj?.color || "#ffc933"}88`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate("/profile/deposit")}
                data-testid="vip-levelup-cta"
                className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm shrink-0 transition-all hover:scale-[1.03]"
                style={{
                  background:
                    "linear-gradient(135deg, #ffc933 0%, #f59e0b 100%)",
                  color: "#0a1119",
                  boxShadow:
                    "0 10px 28px rgba(255,201,51,0.40), inset 0 1px 0 rgba(255,255,255,0.3)",
                }}
              >
                Level Up <ArrowRight size={16} />
              </button>
            </div>
          </section>
        )}

        {/* TIER CARDS GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-7 sm:mb-10">
          {tiers.map((t, i) => (
            <VipTier
              key={t.name}
              {...t}
              isCurrent={user && currentTier === t.name}
              isFlagship={i === tiers.length - 1}
              index={i}
            />
          ))}
        </div>

        {/* HOW TO CLIMB — premium step layout */}
        <section className="mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-5">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#ffc933] mb-1">
              ✦ Your journey
            </div>
            <h2
              className="text-white text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              How to climb the{" "}
              <span
                className="italic"
                style={{
                  background:
                    "linear-gradient(135deg, #ffc933 0%, #a855f7 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                VIP ladder
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {VIP_LADDER_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.n}
                  className="group relative rounded-2xl border border-white/[0.07] p-5 sm:p-6 overflow-hidden hover:border-white/20 transition-all"
                  style={{
                    background:
                      "linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
                  }}
                >
                  <div
                    aria-hidden
                    className="absolute -top-12 -right-12 h-36 w-36 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                    style={{ background: s.color }}
                  />
                  <div className="relative flex items-start justify-between mb-4">
                    <div
                      className="text-5xl sm:text-6xl font-black leading-none tracking-tight"
                      style={{
                        background: `linear-gradient(135deg, ${s.color} 0%, rgba(255,255,255,0.05) 100%)`,
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        fontFamily: "'Playfair Display', Georgia, serif",
                      }}
                    >
                      {s.n}
                    </div>
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center"
                      style={{
                        background: `${s.color}1f`,
                        boxShadow: `inset 0 0 0 1px ${s.color}55`,
                      }}
                    >
                      <Icon size={16} style={{ color: s.color }} />
                    </div>
                  </div>
                  <h3 className="relative text-white font-bold text-lg sm:text-xl tracking-tight leading-tight">
                    {s.t}
                  </h3>
                  <p className="relative text-white/55 text-sm mt-1.5 leading-relaxed">
                    {s.d}
                  </p>
                  {i < VIP_LADDER_STEPS.length - 1 && (
                    <div
                      aria-hidden
                      className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full items-center justify-center"
                      style={{
                        background: "#0a111c",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <ChevronRight size={14} className="text-white/35" />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {/* EXCLUSIVE PERKS RIBBON */}
        <section
          className="relative rounded-2xl sm:rounded-3xl border border-white/[0.07] p-6 sm:p-8 overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 100% at 100% 0%, rgba(255,201,51,0.12), transparent 55%), radial-gradient(80% 60% at 0% 100%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
          }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-center">
            {[
              { icon: Gem, label: "Bespoke Bonuses", color: "#22d3ee" },
              { icon: Trophy, label: "Private Tournaments", color: "#ffc933" },
              { icon: Sparkles, label: "Luxury Travel", color: "#ec4899" },
              { icon: HeadphonesIcon, label: "VIP Host", color: "#a855f7" },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} className="flex flex-col items-center gap-2 sm:gap-3">
                  <div
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background: `${p.color}1f`,
                      boxShadow: `inset 0 0 0 1px ${p.color}55`,
                    }}
                  >
                    <Icon size={20} style={{ color: p.color }} />
                  </div>
                  <div className="text-white text-xs sm:text-sm font-bold leading-tight">
                    {p.label}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
};

/* =============================================================
   REFERRAL PAGE — full marketing redesign
   ============================================================= */
export const ReferralPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { settings } = useSiteSettings() || {};
  const commissionPct = Number(settings?.referral_bonus_pct ?? 30);
  const signupBonus = Number(settings?.referral_signup_bonus ?? 500);
  const accent = "#19a55c";

  const goCta = () => {
    if (user) navigate("/profile/referral");
    else navigate("/register");
  };

  const stats = [
    { v: `৳${signupBonus.toLocaleString("en-IN")}`, l: "Per friend signup", icon: Gift },
    { v: `${commissionPct}%`, l: "Lifetime commission", icon: Trophy },
    { v: "Instant", l: "Auto-credit on bets", icon: Zap },
    { v: "Unlimited", l: "Friends you can invite", icon: Users },
  ];

  const steps = [
    { n: "01", t: "Grab your link", d: "Open Profile → My Referral and copy your unique invite link or 6-digit code.", icon: QrCode, color: "#19a55c" },
    { n: "02", t: "Share with friends", d: "Send via WhatsApp, Telegram, Facebook, or any messenger — one tap and done.", icon: Send, color: "#3b82f6" },
    { n: "03", t: "They sign up + deposit", d: "Each friend who registers and makes their first deposit unlocks your reward.", icon: Users, color: "#a855f7" },
    { n: "04", t: "You earn forever", d: `Get ৳${signupBonus} signup bonus + ${commissionPct}% lifetime commission on every bet they place.`, icon: Trophy, color: "#ffc933" },
  ];

  const faq = [
    { q: "When do I get paid?", a: "The signup bonus credits to your wallet the moment your friend's first deposit completes. The commission is auto-credited to your balance every time they place a bet — no manual claim needed." },
    { q: "How many friends can I refer?", a: "There is no limit. The more friends you bring in, the more you earn — top referrers earn six-figure monthly commissions on Maxlive88." },
    { q: "Where can I see my earnings?", a: "Inside your account, go to Profile → My Referral. You'll see total invitees, lifetime earnings, and a list of every commission credit." },
    { q: "Is there an expiry on commissions?", a: "No — commissions are lifetime. As long as your referred friend keeps playing, you keep earning. Forever." },
  ];

  return (
    <SiteLayout>
      <div data-testid="referral-page" className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        {/* HERO */}
        <section
          className="relative overflow-hidden rounded-3xl border border-white/5 p-6 sm:p-12"
          style={{
            background:
              "radial-gradient(circle at 100% 0%, rgba(25,165,92,0.35) 0%, transparent 55%), radial-gradient(circle at 0% 100%, rgba(255,201,51,0.18) 0%, transparent 55%), linear-gradient(160deg, #0f1f17 0%, #0a1119 60%, #0f1f17 100%)",
          }}
        >
          {/* Decorative grain */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #fff 0px, #fff 1px, transparent 1px, transparent 6px)",
            }}
          />
          <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-center">
            <div>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-widest uppercase"
                style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}55` }}
              >
                <Sparkles size={12} /> Referral Program
              </span>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white mt-4 leading-[1.02]">
                Invite friends.
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(135deg, #c8ffe5 0%, ${accent} 50%, #0f5e34 100%)` }}
                >
                  Earn forever.
                </span>
              </h1>
              <p className="text-white/65 mt-5 max-w-xl text-base sm:text-lg leading-relaxed">
                Share Maxlive88 with friends — pocket{" "}
                <strong className="text-white">৳{signupBonus.toLocaleString("en-IN")}</strong>{" "}
                for every signup, plus a{" "}
                <strong className="text-white">{commissionPct}% lifetime commission</strong>{" "}
                on every bet they place. No caps. No expiry. Auto-credited to your wallet.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={goCta}
                  data-testid="referral-cta-primary"
                  className="group h-12 px-6 rounded-xl font-bold text-sm sm:text-base inline-flex items-center gap-2 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${accent} 0%, #0f5e34 100%)`,
                    color: "#fff",
                    boxShadow: `0 10px 28px ${accent}55`,
                  }}
                >
                  {user ? "Go to my referral dashboard" : "Get my referral link"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/profile/affiliate-apply")}
                  data-testid="referral-cta-affiliate"
                  className="h-12 px-5 rounded-xl font-semibold text-sm sm:text-base inline-flex items-center gap-2 border border-white/15 text-white/85 hover:bg-white/5 transition-colors"
                >
                  Become an affiliate
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>

            {/* Right visual card */}
            <div className="relative">
              <div
                className="rounded-2xl p-6 sm:p-7 relative overflow-hidden"
                style={{
                  background: "linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="text-white/45 text-[11px] uppercase tracking-widest font-semibold">Your earnings preview</div>
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                </div>

                <div className="space-y-3">
                  {[
                    { label: "5 friends signed up", value: `+৳${(signupBonus * 5).toLocaleString("en-IN")}`, hint: "Signup bonuses" },
                    { label: "Their total bets ৳1,00,000", value: `+৳${((100000 * commissionPct) / 100).toLocaleString("en-IN")}`, hint: `${commissionPct}% lifetime commission` },
                    { label: "Withdrawn this month", value: `৳${(signupBonus * 5 + (100000 * commissionPct) / 100).toLocaleString("en-IN")}`, hint: "Auto-credited", highlight: true },
                  ].map((row, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between p-3 rounded-xl ${
                        row.highlight
                          ? "bg-emerald-500/10 border border-emerald-500/30"
                          : "bg-white/[0.03] border border-white/5"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-white text-sm font-semibold truncate">{row.label}</div>
                        <div className="text-white/40 text-[11px] mt-0.5">{row.hint}</div>
                      </div>
                      <div
                        className={`tabular-nums font-extrabold text-base ${
                          row.highlight ? "text-emerald-300" : "text-white"
                        }`}
                      >
                        {row.value}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-white/40 text-center mt-4">Illustrative example — actual earnings depend on your friends' activity</div>
              </div>
              {/* Glow */}
              <div
                aria-hidden
                className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full opacity-50 pointer-events-none blur-3xl"
                style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
              />
            </div>
          </div>
        </section>

        {/* STATS strip */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-5 sm:mt-7">
          {stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl p-4 sm:p-5 border border-white/5 relative overflow-hidden group"
              style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)" }}
              data-testid={`referral-stat-${i}`}
            >
              <div
                className="h-9 w-9 rounded-lg inline-flex items-center justify-center mb-3"
                style={{ background: `${accent}20`, color: accent }}
              >
                <s.icon size={17} />
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{s.v}</div>
              <div className="text-white/50 text-xs sm:text-sm mt-1">{s.l}</div>
            </div>
          ))}
        </section>

        {/* HOW IT WORKS */}
        <section className="mt-7 sm:mt-12">
          <div className="flex items-end justify-between mb-5 sm:mb-7">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: accent }}>
                How it works
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1 tracking-tight">
                4 simple steps. Zero limits.
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {steps.map((s) => (
              <div
                key={s.n}
                className="rounded-2xl p-5 border border-white/5 relative overflow-hidden h-full transition-all hover:border-white/15 hover:-translate-y-0.5"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.25) 100%)",
                }}
                data-testid={`referral-step-${s.n}`}
              >
                <div
                  aria-hidden
                  className="absolute -top-6 -right-6 h-24 w-24 rounded-full opacity-20 blur-2xl"
                  style={{ background: `radial-gradient(circle, ${s.color} 0%, transparent 70%)` }}
                />
                <div className="flex items-center justify-between mb-4 relative">
                  <div
                    className="h-10 w-10 rounded-xl inline-flex items-center justify-center"
                    style={{ background: `${s.color}20`, color: s.color, border: `1px solid ${s.color}55` }}
                  >
                    <s.icon size={18} />
                  </div>
                  <span
                    className="font-display text-3xl tabular-nums font-extrabold"
                    style={{ color: `${s.color}55` }}
                  >
                    {s.n}
                  </span>
                </div>
                <h3 className="text-white font-bold text-base relative">{s.t}</h3>
                <p className="text-white/55 text-sm mt-1.5 leading-relaxed relative">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* BIG CTA strip */}
        <section
          className="mt-7 sm:mt-12 rounded-3xl p-6 sm:p-10 relative overflow-hidden border border-white/5"
          style={{
            background:
              "radial-gradient(circle at 0% 50%, rgba(25,165,92,0.35) 0%, transparent 50%), linear-gradient(135deg, #0a1119 0%, #102018 100%)",
          }}
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center relative">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Ready to turn your network into income?
              </h3>
              <p className="text-white/60 mt-2 text-sm sm:text-base max-w-2xl">
                Top referrers on Maxlive88 earn ৳50,000+ monthly — purely from inviting friends.
                Start today and watch your wallet grow with every bet they place.
              </p>
            </div>
            <button
              type="button"
              onClick={goCta}
              data-testid="referral-cta-bottom"
              className="h-12 px-6 rounded-xl font-bold text-sm sm:text-base inline-flex items-center gap-2 transition-all whitespace-nowrap"
              style={{
                background: "#fff",
                color: "#0a1119",
                boxShadow: "0 10px 28px rgba(255,255,255,0.18)",
              }}
            >
              {user ? "Open my dashboard" : "Get started — it's free"}
              <ArrowRight size={16} />
            </button>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-7 sm:mt-12">
          <div className="text-[11px] uppercase tracking-widest font-bold mb-4 sm:mb-5" style={{ color: accent }}>
            Frequently asked
          </div>
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
            {faq.map((f, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 border border-white/5"
                style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.15) 100%)" }}
                data-testid={`referral-faq-${i}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-6 w-6 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${accent}20`, color: accent }}
                  >
                    <CheckCircle2 size={14} />
                  </span>
                  <div>
                    <h4 className="text-white font-semibold text-sm sm:text-base">{f.q}</h4>
                    <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
};

/* =============================================================
   APP DOWNLOAD PAGE
   ============================================================= */
export const AppDownloadPage = () => {
  const { settings } = useSiteSettings() || {};
  const onApk = () => {
    const url = settings?.apk_url || "#";
    if (!url || url === "#") {
      toast.info("APK will be available soon");
      return;
    }
    window.open(url, "_blank");
  };
  const features = settings?.apk_features?.length ? settings.apk_features : [
    { icon: "Zap",         title: "2× Faster Loading",   color: "#ffc933" },
    { icon: "Star",        title: "Exclusive App Bonus", color: "#19a55c" },
    { icon: "ShieldCheck", title: "Bank-grade Secure",   color: "#3b82f6" },
    { icon: "Sparkles",    title: "Real-time Alerts",    color: "#ec4899" },
  ];
  const steps = settings?.apk_steps?.length ? settings.apk_steps : [
    { n: "1", title: "Download APK",   desc: "Tap the download button — the .apk file saves to your device." },
    { n: "2", title: "Enable installs",desc: "In settings, allow \"Install unknown apps\" for your browser." },
    { n: "3", title: "Open & Play",    desc: "Tap the downloaded file, install, sign in and start winning." },
  ];
  const appName = settings?.site_name || "Maxlive88";
  const appVersion = settings?.apk_version || "v2.0";
  const appSize = settings?.apk_size_mb || "32 MB";
  const rating = settings?.apk_rating || "4.8";
  const downloads = settings?.apk_downloads || "500k+";
  return (
    <SiteLayout>
      <div data-testid="apk-page" className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 py-5 sm:py-10">
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-10 items-center">
          {/* Left: copy + CTAs */}
          <div className="relative">
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wider"
              style={{ background: "#19a55c20", color: "#19a55c", border: "1px solid #19a55c55" }}
            >
              <Smartphone size={12} /> ANDROID APP
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-3 sm:mt-4 leading-[1.05]">
              Get the{" "}
              <span className="bg-gradient-to-br from-[#c8ffe5] via-[#19a55c] to-[#0f5e34] bg-clip-text text-transparent">
                {appName}
              </span>
              <br />
              Official App
            </h1>
            <p className="text-white/65 mt-3 sm:mt-4 text-sm sm:text-base max-w-lg leading-relaxed">
              Faster, smoother, and built for serious players. One-tap login, push
              notifications for big wins, and every casino/sports game — right in your pocket.
            </p>

            {/* Feature list */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-5 sm:mt-7 max-w-md">
              {features.map((f, i) => {
                const Icon = resolveIcon(f.icon, Zap);
                return (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-[#121a26] border border-white/5 p-2.5 sm:p-3">
                    <div
                      className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center"
                      style={{ background: `${f.color}1a`, boxShadow: `inset 0 0 0 1px ${f.color}50` }}
                    >
                      <Icon size={14} style={{ color: f.color }} />
                    </div>
                    <span className="text-[11.5px] sm:text-[13px] text-white/85 font-semibold leading-tight">
                      {f.title}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-2.5 sm:gap-3 mt-6 sm:mt-8">
              <button
                onClick={onApk}
                data-testid="apk-download-btn"
                className="inline-flex items-center gap-2 h-12 sm:h-14 px-5 sm:px-6 rounded-xl bg-gradient-to-r from-[#19a55c] to-[#168f4f] hover:from-[#21c46e] hover:to-[#19a55c] text-white font-extrabold text-sm sm:text-base transition-colors shadow-[0_8px_24px_rgba(25,165,92,0.45)]"
              >
                <Download size={18} />
                Download APK
                <span className="text-white/70 text-[10px] sm:text-xs font-semibold ml-1">
                  {appVersion} · {appSize}
                </span>
              </button>
              <button
                onClick={() => toast.info("iOS app coming soon — stay tuned")}
                className="inline-flex items-center gap-2 h-12 sm:h-14 px-5 sm:px-6 rounded-xl border border-white/15 hover:border-white/30 hover:bg-white/5 font-bold text-sm sm:text-base transition-colors"
              >
                <Smartphone size={18} />
                iOS — Coming soon
              </button>
            </div>

            {/* trust */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-5 mt-5 sm:mt-6 text-[11px] sm:text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5">
                <Star size={12} className="text-[#ffc933] fill-[#ffc933]" /> {rating} Rating
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users size={12} className="text-[#19a55c]" /> {downloads} Downloads
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-[#3b82f6]" /> Virus-free
              </span>
            </div>
          </div>

          {/* Right: phone mockup + QR */}
          <div className="relative flex justify-center">
            <div
              className="relative aspect-[9/16] w-full max-w-[300px] rounded-[44px] p-3 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
              style={{
                background: "linear-gradient(160deg, #1f2937 0%, #0a1119 100%)",
                border: "2px solid rgba(255,255,255,0.08)",
              }}
            >
              {/* notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 h-5 w-24 rounded-b-2xl bg-black z-10" />
              <div
                className="relative h-full w-full rounded-[36px] overflow-hidden flex items-center justify-center"
                style={{
                  background: "radial-gradient(120% 80% at 30% 20%, #1a2a25 0%, #0c1117 70%)",
                }}
              >
                {/* App icon big */}
                <div className="text-center">
                  <div
                    className="mx-auto h-24 w-24 sm:h-32 sm:w-32 rounded-3xl flex items-center justify-center mb-4 shadow-[0_12px_40px_rgba(25,165,92,0.55)]"
                    style={{
                      background: "linear-gradient(135deg,#19a55c 0%,#0f5e34 100%)",
                      border: "2px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <span className="font-display text-5xl sm:text-6xl text-white tracking-tight">{appName[0]}</span>
                  </div>
                  <div className="font-display text-2xl sm:text-3xl text-white">
                    {appName}
                  </div>
                  <div className="text-white/40 text-[11px] sm:text-xs mt-1">{settings?.footer_brand_tagline || "Play Smart, Win Big"}</div>
                  <div
                    className="mx-auto mt-6 h-11 w-44 rounded-full flex items-center justify-center gap-2 font-bold text-sm"
                    style={{ background: "#19a55c", color: "white" }}
                  >
                    <Download size={14} /> Install
                  </div>
                </div>
              </div>
            </div>

            {/* QR code badge */}
            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-10 flex items-center gap-3 rounded-2xl bg-[#121a26] border border-white/10 p-3 sm:p-4 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-lg bg-white flex items-center justify-center">
                <QrCode size={40} className="text-black" />
              </div>
              <div className="leading-tight">
                <div className="text-white font-bold text-xs sm:text-sm">Scan to install</div>
                <div className="text-white/50 text-[10px] sm:text-[11px] mt-0.5">Open camera</div>
              </div>
            </div>
          </div>
        </div>

        {/* Install Steps */}
        <div className="mt-10 sm:mt-16">
          <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-4 sm:mb-5">How to install</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {steps.map((s) => (
              <div
                key={s.n}
                className="relative rounded-xl border border-white/5 bg-[#0f1722] p-4 sm:p-5 overflow-hidden"
              >
                <div
                  className="absolute -right-4 -top-4 font-display text-5xl sm:text-6xl text-[#19a55c]/15 leading-none select-none"
                >
                  {s.n}
                </div>
                <div className="relative h-9 w-9 rounded-lg bg-[#19a55c]/15 border border-[#19a55c]/40 flex items-center justify-center font-extrabold text-[#19a55c]">
                  {s.n}
                </div>
                <h4 className="relative font-bold text-white mt-2.5 text-sm sm:text-base">{s.title}</h4>
                <p className="relative text-[12.5px] sm:text-[13px] text-white/55 mt-1 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
};

/* =============================================================
   CONTACT US PAGE
   ============================================================= */
const ContactChannel = ({ icon: Icon, title, sub, cta, ctaLabel, color, testId, href }) => {
  const isExternal = href && /^https?:/i.test(href);
  const ctaProps = href
    ? {
        as: "a",
        href,
        target: isExternal ? "_blank" : undefined,
        rel: isExternal ? "noopener noreferrer" : undefined,
      }
    : { onClick: cta };

  const CtaTag = href ? "a" : "button";
  return (
    <div
      className="group relative rounded-2xl border border-white/5 bg-gradient-to-br from-[#121a26] to-[#0a1119] p-4 sm:p-5 hover:border-white/15 hover:-translate-y-0.5 transition-all overflow-hidden"
      data-testid={testId}
    >
      <div
        aria-hidden
        className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-15 blur-2xl group-hover:opacity-30 transition-opacity"
        style={{ background: color }}
      />
      <div
        className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center mb-2.5 sm:mb-3 transition-transform group-hover:scale-110"
        style={{ background: `${color}20`, boxShadow: `inset 0 0 0 1px ${color}55` }}
      >
        <Icon size={18} style={{ color }} className="sm:hidden" />
        <Icon size={22} style={{ color }} className="hidden sm:block" />
      </div>
      <h3 className="relative text-white font-bold text-[14px] sm:text-base">{title}</h3>
      <p className="relative text-white/55 text-[11.5px] sm:text-[13px] mt-1 leading-relaxed min-h-[34px] sm:min-h-[40px]">
        {sub}
      </p>
      <CtaTag
        {...(href
          ? { href, target: isExternal ? "_blank" : undefined, rel: isExternal ? "noopener noreferrer" : undefined }
          : { onClick: cta })}
        className="relative mt-3 inline-flex items-center gap-1.5 h-9 sm:h-10 px-3 sm:px-4 rounded-md text-[12px] sm:text-sm font-bold transition-colors no-underline"
        style={{
          background: `${color}20`,
          color,
          border: `1px solid ${color}45`,
        }}
        data-testid={`${testId}-cta`}
      >
        {ctaLabel} <ArrowRight size={13} />
      </CtaTag>
    </div>
  );
};

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen((o) => !o)}
      className="w-full text-left rounded-xl border border-white/5 bg-[#0f1722] hover:border-white/15 p-3.5 sm:p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-white font-semibold text-[13px] sm:text-sm">{q}</span>
        <ChevronRight
          size={16}
          className={`shrink-0 text-white/40 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </div>
      {open && (
        <p className="text-white/60 text-[12px] sm:text-[13px] mt-2 leading-relaxed">{a}</p>
      )}
    </button>
  );
};

export const ContactPage = () => {
  const { settings } = useSiteSettings() || {};
  const email = settings?.support_email || "support@maxlive88.com";
  const phone = settings?.support_phone || "+880-1700-000000";

  // --- Build Telegram URL robustly (admin may save "@user", "user", or full URL) ---
  const buildTelegramUrl = () => {
    const direct = (settings?.telegram_channel_url || "").trim();
    if (direct) {
      return direct.startsWith("http") ? direct : `https://${direct.replace(/^\/+/, "")}`;
    }
    let u = (settings?.telegram_username || "maxlive88official").trim();
    u = u.replace(/^https?:\/\/(t\.me|telegram\.me)\//i, "");
    u = u.replace(/^t\.me\//i, "").replace(/^@+/, "").replace(/^\/+/, "");
    return u ? `https://t.me/${u}` : "";
  };

  // --- Build WhatsApp URL robustly ---
  const buildWhatsappUrl = () => {
    const raw = (settings?.whatsapp_number || phone || "").replace(/\D/g, "");
    return raw ? `https://wa.me/${raw}` : "";
  };

  const telegramUrl = buildTelegramUrl();
  const whatsappUrl = buildWhatsappUrl();

  const channels = [
    {
      icon: MessageCircle,
      title: "Live Chat",
      sub: "Real agents online now — avg. response under 1 minute.",
      cta: () => toast.info("Opening live chat..."),
      ctaLabel: "Start Chat",
      color: "#19a55c",
      testId: "contact-livechat",
    },
    {
      icon: Send,
      title: "Telegram",
      sub: `Official channel — announcements, bonuses & fast support.`,
      href: telegramUrl,
      cta: () => {
        if (!telegramUrl) return toast.error("Telegram link not configured yet");
        window.open(telegramUrl, "_blank", "noopener,noreferrer");
      },
      ctaLabel: "Open Telegram",
      color: "#26a5e4",
      testId: "contact-telegram",
    },
    {
      icon: Phone,
      title: "WhatsApp",
      sub: `24/7 Bangla-speaking support on your favourite messenger.`,
      href: whatsappUrl,
      cta: () => {
        if (!whatsappUrl) return toast.error("WhatsApp number not configured yet");
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      },
      ctaLabel: "Open WhatsApp",
      color: "#25d366",
      testId: "contact-whatsapp",
    },
    {
      icon: Mail,
      title: "Email",
      sub: `Written queries, disputes & account issues — replies within 4 hours.`,
      href: `mailto:${email}`,
      cta: () => { window.location.href = `mailto:${email}`; },
      ctaLabel: `Email Us`,
      color: "#ffc933",
      testId: "contact-email",
    },
    {
      icon: Trophy,
      title: "VIP Support",
      sub: "Dedicated host for Gold, Platinum and Diamond tier members.",
      cta: () => toast.info("VIP support opens after login"),
      ctaLabel: "Contact Host",
      color: "#a855f7",
      testId: "contact-vip",
    },
    {
      icon: Phone,
      title: "Phone",
      sub: `Prefer to talk? Call our hotline during business hours.`,
      href: `tel:${phone}`,
      cta: () => { window.location.href = `tel:${phone}`; },
      ctaLabel: phone,
      color: "#ec4899",
      testId: "contact-phone",
    },
  ];

  const faqs = (settings?.contact_faqs?.length ? settings.contact_faqs : [
    { q: "How long does withdrawal take?",         a: "bKash/Nagad/Rocket withdrawals are typically processed within 2-24 hours after admin approval. Bank transfers may take up to 48 hours." },
    { q: "What is the minimum deposit amount?",    a: "The minimum deposit is ৳100 via local payment methods (bKash, Nagad, Rocket, UPay)." },
    { q: "Do I need to verify my account?",        a: "KYC verification is only required for withdrawals above ৳25,000. Smaller amounts can be withdrawn instantly after admin approval." },
    { q: "Can I have multiple accounts?",          a: "No, per our Terms of Service each player may have only one account. Multiple accounts will be closed and balances may be forfeited." },
    { q: "Is Maxlive88 licensed?",                 a: "Yes — Maxlive88 operates under the Autonomous Island of Anjouan gaming license (ALSI-202410030-FI1)." },
  ]);

  return (
    <SiteLayout>
      <div data-testid="contact-page" className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 py-5 sm:py-8">
        <PageHero
          icon={HeadphonesIcon}
          kicker="We're here 24/7"
          title="Contact Us"
          subtitle="Reach out anytime — we support you through every channel you use."
          accent="#3b82f6"
        />

        {/* Hours banner */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5 sm:mb-7">
          {[
            { i: Clock, t: settings?.contact_stat_hours_value || "24/7",   s: settings?.contact_stat_hours_label || "Support Hours",   c: "#19a55c" },
            { i: Zap,   t: settings?.contact_stat_chat_value  || "< 1 min", s: settings?.contact_stat_chat_label  || "Live Chat Avg.", c: "#ffc933" },
            { i: Mail,  t: settings?.contact_stat_email_value || "< 4 h",   s: settings?.contact_stat_email_label || "Email Reply",    c: "#3b82f6" },
          ].map((b, i) => {
            const Icon = b.i;
            return (
              <div key={i} className="rounded-xl bg-[#121a26] border border-white/5 p-2 sm:p-4 text-center min-w-0">
                <Icon size={16} style={{ color: b.c }} className="mx-auto mb-1 sm:hidden" />
                <Icon size={22} style={{ color: b.c }} className="mx-auto mb-1.5 hidden sm:block" />
                <div className="text-[13px] sm:text-xl font-extrabold text-white truncate" title={b.t}>{b.t}</div>
                <div className="text-[9px] sm:text-xs text-white/50 uppercase tracking-wider mt-0.5 truncate">
                  {b.s}
                </div>
              </div>
            );
          })}
        </div>

        {/* Channel grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
          {channels.map((c) => (
            <ContactChannel key={c.testId} {...c} />
          ))}
        </div>

        {/* Social row */}
        <div className="mt-7 sm:mt-10 rounded-2xl border border-white/5 bg-[#0f1722] p-4 sm:p-6">
          <h3 className="text-white font-extrabold text-base sm:text-lg mb-3 sm:mb-4">Follow us</h3>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {[
              { i: Facebook, l: "Facebook", c: "#1877f2", u: settings?.facebook_url },
              { i: Instagram, l: "Instagram", c: "#e1306c", u: settings?.instagram_url },
              { i: Send, l: "Telegram", c: "#26a5e4", u: telegramUrl },
              { i: MessageCircle, l: "WhatsApp", c: "#25d366", u: whatsappUrl },
            ].map((s, i) => {
              const Icon = s.i;
              return (
                <a
                  key={i}
                  href={s.u || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 h-10 sm:h-11 px-3 sm:px-4 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 text-[12px] sm:text-sm font-semibold text-white transition-colors"
                >
                  <Icon size={15} style={{ color: s.c }} />
                  {s.l}
                </a>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-7 sm:mt-10">
          <h3 className="flex items-center gap-2 text-xl sm:text-2xl font-extrabold text-white mb-3 sm:mb-4">
            <Sparkles size={18} className="text-[#ffc933]" /> Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
            {faqs.map((f, i) => (
              <FAQItem key={i} {...f} />
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
};

/* =============================================================
   AMBASSADORS PAGE — Premium editorial redesign
   ============================================================= */

const PremiumAmbassadorCard = ({ a, featured = false, index = 0 }) => (
  <div
    data-testid={`ambassador-card-${a.id}`}
    className={`group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-[#ec4899]/40 transition-all duration-500 ${
      featured ? "sm:col-span-2 sm:row-span-2" : ""
    }`}
    style={{
      background:
        "linear-gradient(180deg, #0c1220 0%, #131b2c 60%, #0a0f1a 100%)",
      boxShadow:
        "0 8px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      animation: `ambassadorRise 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${
        index * 80
      }ms backwards`,
    }}
  >
    {/* Corner decorative monogram */}
    <div
      aria-hidden
      className="absolute top-3 left-3 z-20 h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-black tracking-wider"
      style={{
        background: "rgba(236,72,153,0.12)",
        border: "1px solid rgba(236,72,153,0.35)",
        color: "#f9a8d4",
        backdropFilter: "blur(6px)",
      }}
    >
      M88
    </div>

    {/* Verified badge */}
    <div
      aria-hidden
      className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider"
      style={{
        background: "rgba(255,201,51,0.12)",
        border: "1px solid rgba(255,201,51,0.35)",
        color: "#ffc933",
        backdropFilter: "blur(6px)",
      }}
    >
      <BadgeCheck size={10} /> Official
    </div>

    {/* Diagonal pattern overlay */}
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, #ec4899 0 1px, transparent 1px 12px)",
      }}
    />

    {/* Pink radial glow on hover */}
    <div
      aria-hidden
      className="absolute -top-12 -right-12 h-44 w-44 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
      style={{ background: "radial-gradient(circle, #ec4899 0%, transparent 70%)" }}
    />

    {/* Image stage */}
    <div
      className={`relative overflow-hidden ${
        featured ? "aspect-[4/5] sm:aspect-[5/6]" : "aspect-[3/4]"
      }`}
      style={{
        background:
          "radial-gradient(ellipse at 50% 30%, #1d2740 0%, #0a0f1a 80%)",
      }}
    >
      {/* Decorative spotlight ring */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 group-hover:opacity-60 transition-opacity duration-700"
        style={{
          width: featured ? "85%" : "78%",
          aspectRatio: "1",
          background:
            "radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 65%)",
          filter: "blur(20px)",
        }}
      />
      {/* Subtle vignette frame */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow:
            "inset 0 -80px 80px -20px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.03)",
        }}
      />

      {a.photo ? (
        <>
          {/* Portrait photo as main image */}
          <img
            src={a.photo}
            alt={a.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="absolute inset-0 z-10 w-full h-full object-cover object-top group-hover:scale-[1.06] transition-transform duration-[900ms]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          {/* Color grade + dark gradient on bottom */}
          <div
            aria-hidden
            className="absolute inset-0 z-20 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent 30%, rgba(7,16,28,0.55) 70%, rgba(7,16,28,0.92) 100%), radial-gradient(120% 80% at 50% 20%, transparent 40%, rgba(236,72,153,0.10) 100%)",
            }}
          />
          {/* Signature overlay watermark */}
          <img
            src={a.img}
            alt=""
            aria-hidden
            loading="lazy"
            className={`absolute z-30 pointer-events-none ${
              featured
                ? "right-4 bottom-16 sm:right-6 sm:bottom-20 w-32 sm:w-44"
                : "right-3 bottom-12 w-20 sm:w-24"
            } opacity-90 group-hover:opacity-100 transition-opacity`}
            style={{
              filter:
                "drop-shadow(0 2px 8px rgba(0,0,0,0.7)) drop-shadow(0 0 12px rgba(236,72,153,0.35))",
            }}
          />
        </>
      ) : (
        // Fallback: signature only (when no portrait available)
        <img
          src={a.img}
          alt={a.name}
          loading="lazy"
          className="relative z-10 w-full h-full object-contain p-6 sm:p-8 group-hover:scale-[1.04] transition-transform duration-700"
          style={{
            filter:
              "drop-shadow(0 8px 28px rgba(236,72,153,0.25)) drop-shadow(0 2px 6px rgba(0,0,0,0.5))",
          }}
        />
      )}

      {/* Corner accents */}
      {["top-left", "top-right", "bottom-left", "bottom-right"].map((pos) => {
        const map = {
          "top-left": "top-2 left-2 border-l border-t",
          "top-right": "top-2 right-2 border-r border-t",
          "bottom-left": "bottom-2 left-2 border-l border-b",
          "bottom-right": "bottom-2 right-2 border-r border-b",
        };
        return (
          <span
            key={pos}
            aria-hidden
            className={`absolute z-40 h-4 w-4 ${map[pos]} pointer-events-none transition-all duration-500 group-hover:h-6 group-hover:w-6`}
            style={{ borderColor: "rgba(255,201,51,0.55)" }}
          />
        );
      })}
    </div>

    {/* Bottom name plate */}
    <div className="relative px-4 sm:px-5 py-3.5 sm:py-4 border-t border-white/[0.06]">
      <div
        aria-hidden
        className="absolute inset-x-4 sm:inset-x-5 -top-px h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, #ec4899 50%, transparent 100%)",
        }}
      />
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Star size={10} className="text-[#ffc933] fill-[#ffc933]" />
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#f9a8d4] font-bold truncate">
              {a.role || "Brand Ambassador"}
            </span>
          </div>
          <h3
            className={`text-white font-black tracking-tight leading-tight truncate ${
              featured ? "text-xl sm:text-2xl" : "text-base sm:text-lg"
            }`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {a.name}
          </h3>
          <p className="text-[11px] sm:text-xs text-white/45 mt-1 font-mono tracking-wider">
            {a.year}
          </p>
        </div>
        <ChevronRight
          size={16}
          className="text-white/30 group-hover:text-[#ec4899] group-hover:translate-x-1 transition-all duration-300 shrink-0"
        />
      </div>
    </div>
  </div>
);

const AmbassadorStat = ({ value, label, icon: Icon, color }) => (
  <div
    className="relative rounded-xl border border-white/[0.06] px-4 py-3 sm:px-5 sm:py-4 overflow-hidden"
    style={{
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 60%), #0e1522",
    }}
  >
    <div
      aria-hidden
      className="absolute -top-6 -right-6 h-20 w-20 rounded-full blur-2xl opacity-25"
      style={{ background: color }}
    />
    <div className="relative flex items-center gap-3">
      <div
        className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center"
        style={{
          background: `${color}1f`,
          boxShadow: `inset 0 0 0 1px ${color}55`,
        }}
      >
        <Icon size={16} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-white text-lg sm:text-xl font-black leading-none">
          {value}
        </div>
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/50 mt-1 font-semibold">
          {label}
        </div>
      </div>
    </div>
  </div>
);

export const AmbassadorsPage = () => {
  const ACCENT = "#ec4899";
  const [featured, ...rest] = AMBASSADORS;

  return (
    <SiteLayout>
      {/* Inline keyframes — local to this page */}
      <style>{`
        @keyframes ambassadorRise {
          0% { opacity: 0; transform: translateY(18px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shimmerLine {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div
        className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 py-5 sm:py-8"
        data-testid="ambassadors-page"
      >
        {/* HERO — editorial style */}
        <section
          className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
          style={{
            background:
              "radial-gradient(120% 100% at 0% 0%, rgba(236,72,153,0.18), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(255,201,51,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
          }}
        >
          {/* Decorative halftone */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle, #ffffff 1px, transparent 1.2px)",
              backgroundSize: "22px 22px",
            }}
          />
          {/* Sparkle blobs */}
          <div
            aria-hidden
            className="absolute -top-20 -right-10 h-72 w-72 rounded-full opacity-25 blur-3xl"
            style={{ background: ACCENT }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-12 h-72 w-72 rounded-full opacity-15 blur-3xl"
            style={{ background: "#ffc933" }}
          />

          <div className="relative p-6 sm:p-10 lg:p-14">
            {/* Top eyebrow */}
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <span
                className="h-px w-8 sm:w-12"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, #ec4899)",
                }}
              />
              <span
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black"
                style={{ color: ACCENT }}
              >
                Our Faces · Est. 2023
              </span>
              <Sparkles size={12} style={{ color: ACCENT }} />
            </div>

            {/* Title block */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
              <div className="lg:col-span-8">
                <h1
                  className="text-white font-black leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Brand{" "}
                  <span
                    className="italic relative inline-block"
                    style={{
                      background:
                        "linear-gradient(135deg, #ec4899 0%, #f9a8d4 50%, #ffc933 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Ambassadors
                    <span
                      aria-hidden
                      className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, #ec4899 30%, #ffc933 70%, transparent)",
                        backgroundSize: "200% 100%",
                        animation: "shimmerLine 3s linear infinite",
                      }}
                    />
                  </span>
                </h1>
                <p className="text-white/60 mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed">
                  World-class cricket legends, silver-screen icons and esports
                  superstars — proudly representing{" "}
                  <span className="text-white font-semibold">Maxlive88</span>{" "}
                  on the global stage.
                </p>

                <div className="mt-5 sm:mt-7 flex flex-wrap items-center gap-2">
                  {["Cricket", "Cinema", "Esports", "Lifestyle"].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold border border-white/10 text-white/75 bg-white/[0.03] hover:border-[#ec4899]/40 hover:text-white transition-colors"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: ACCENT }}
                      />
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Stats panel */}
              <div className="lg:col-span-4 grid grid-cols-2 gap-2 sm:gap-3">
                <AmbassadorStat
                  value={`${AMBASSADORS.length}+`}
                  label="Global Stars"
                  icon={Star}
                  color="#ec4899"
                />
                <AmbassadorStat
                  value="5"
                  label="Countries"
                  icon={Trophy}
                  color="#ffc933"
                />
                <AmbassadorStat
                  value="12M+"
                  label="Combined Fans"
                  icon={Users}
                  color="#19a55c"
                />
                <AmbassadorStat
                  value="2028"
                  label="Active Era"
                  icon={Crown}
                  color="#a855f7"
                />
              </div>
            </div>
          </div>
        </section>

        {/* GRID — featured + rest */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          <PremiumAmbassadorCard a={featured} featured index={0} />
          {rest.map((a, i) => (
            <PremiumAmbassadorCard key={a.id} a={a} index={i + 1} />
          ))}
        </div>

        {/* MARQUEE — name strip */}
        <section
          className="relative mt-8 sm:mt-12 rounded-2xl border border-white/[0.07] overflow-hidden"
          style={{
            background:
              "linear-gradient(90deg, #0a0f1a 0%, #131b2c 50%, #0a0f1a 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-y-0 left-0 w-24 z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #0a0f1a 0%, transparent 100%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-y-0 right-0 w-24 z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(270deg, #0a0f1a 0%, transparent 100%)",
            }}
          />
          <div
            className="flex items-center gap-12 sm:gap-16 py-5 sm:py-6 whitespace-nowrap"
            style={{
              animation: "marqueeScroll 30s linear infinite",
              width: "max-content",
            }}
          >
            {[...AMBASSADORS, ...AMBASSADORS].map((a, i) => (
              <div
                key={`${a.id}-${i}`}
                className="flex items-center gap-4 sm:gap-5"
              >
                <Sparkles size={14} style={{ color: ACCENT }} />
                <span
                  className="text-2xl sm:text-3xl font-black text-white/15 hover:text-white/40 transition-colors uppercase tracking-[0.18em]"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                  }}
                >
                  {a.name}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* PARTNERSHIP CTA */}
        <section
          className="relative mt-6 sm:mt-8 rounded-2xl sm:rounded-3xl border border-white/[0.07] p-5 sm:p-8 overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 100% at 100% 0%, rgba(236,72,153,0.14), transparent 55%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
            <div className="flex-1">
              <div
                className="text-[11px] uppercase tracking-[0.28em] font-black mb-2"
                style={{ color: "#ffc933" }}
              >
                ✦ Partner with us
              </div>
              <h3 className="text-white text-xl sm:text-2xl font-black leading-tight mb-1.5">
                Want to join the Maxlive88 family?
              </h3>
              <p className="text-white/55 text-sm max-w-xl">
                Sports stars, content creators and influencers — explore
                exclusive partnerships, custom rev-share deals and global
                exposure.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="ambassador-contact-cta"
              className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm shrink-0 transition-all hover:scale-[1.03]"
              style={{
                background:
                  "linear-gradient(135deg, #ec4899 0%, #d33b88 100%)",
                color: "#fff",
                boxShadow:
                  "0 10px 28px rgba(236,72,153,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              Apply Now <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
};
